import PrimaryButton from '@components/atoms/buttons/PrimaryButton';
import GridBox from '@components/atoms/GridBox';
import Spacing from '@components/atoms/Spacing';
import FixedGetQuoteButton from '@components/organisms/buttons/FixedGetQuoteButton';
import SubscribeBlogForm from '@components/organisms/forms/SubscribeBlogForm';
import BlogPostSEO from '@components/organisms/seo/BlogPostSEO';
import ShareBlogPost from '@components/organisms/ShareBlogPost';
import Section from 'components/atoms/Section';
import { Heading1, Heading3, Paragraph } from 'components/atoms/Typography';
import CategoryLink from 'components/molecules/CategoryLink';
import PageContainer from 'components/molecules/PageContainer';
import PostDate from 'components/molecules/PostDate';
import PostItem from 'components/molecules/PostItem';
import SliceZone from 'components/molecules/SliceZone';
import COLORS from 'lib/constants/colors';
import ROUTES from 'lib/constants/routes';
import { trackGetQuoteClick } from 'lib/services/analytics';
import { PrismicClient } from 'lib/services/prismic';
import { GetStaticPaths, GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Prismic from 'prismic-javascript';
import { RichText } from 'prismic-reactjs';
import styled from 'styled-components';
import { Post } from 'types/frontEndTypes';

const Header = styled.header`
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 45%,
    ${COLORS.pastel} 45%,
    ${COLORS.pastel} 45%
  );
  overflow-x: hidden;
  position: relative;
  padding: 60px 20px 0;

  h1 {
    font-size: 56px;
    margin: 16px 0;
  }

  .image-wrapper {
    width: 100%;
    padding: 20px 0 24px;
    max-width: 800px;

    img {
      border-radius: 4px;
      width: 100%;
      height: auto;
      background-color: #ececec;
    }
  }

  .inner {
    max-width: 1240px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .content {
    flex-grow: 0;

    flex-basis: 950px;

    .author {
      font-weight: bold;
    }
  }

  @media screen and (max-width: 768px) {
    background: #fff;

    h1 {
      font-size: 32px;
    }
  }

  @media (min-width: 768px) {
    padding: 60px 0 0 0;
    margin-bottom: 0;

    .inner {
      display: flex;

      align-items: center;
    }

    .content {
      padding: 0 20px 0 40px;
      padding-left: 60px;
    }

    .image-wrapper {
      flex-grow: 1;
      padding: 100px 0;
    }
  }

  @media (min-width: 1200px) {
    .content {
      padding-left: 90px;
    }
  }
`;

const Wrapper = styled(Section)`
  max-width: 960px;
  padding-bottom: 64px;

  @media screen and (max-width: 768px) {
    padding: 20px 20px 64px;
  }
`;

export interface BlogPostPageProps {
  post?: Post;
  related: Post[];
  preview: boolean;
}

const PostPage = ({ post, preview, related }: BlogPostPageProps) => {
  const router = useRouter();

  if (router.isFallback || !post) return <PageContainer loading />;

  return (
    <>
      <PageContainer
        metaDescription={post.data.description}
        showNavBar
        showFooter
        preview={preview}
        customAmpPage
      >
        <BlogPostSEO post={post} />
        <Header>
          <div className="inner">
            <div className="image-wrapper">
              {post.data.image.url && (
                <Image
                  src={post.data.image.url}
                  alt={post.data.image.alt}
                  width={post.data.image?.dimensions?.width || 0}
                  height={post.data.image?.dimensions?.height || 0}
                />
              )}
            </div>
            <div className="content">
              <Link href={ROUTES.blog}>
                <a
                  style={{
                    fontSize: 14,
                    fontWeight: 100,
                    textDecoration: 'underline',
                    display: 'block',
                  }}
                >
                  Voltar
                </a>
              </Link>
              <Heading1>{RichText.asText(post.data.title)}</Heading1>
              <PostDate date={post.first_publication_date} />
              <ShareBlogPost post={post} />
            </div>
          </div>
        </Header>
        <Wrapper>
          <SliceZone sliceZone={post.data.body} />
          <ShareBlogPost post={post} />

          <GridBox
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            style={{
              textAlign: 'center',
              border: '1px solid #ebebeb',
              borderRadius: 4,
              padding: 32,
            }}
          >
            <Heading3>Seja o primeiro a saber!</Heading3>

            <SubscribeBlogForm
              description="Quer receber nossos posts? Deixe seu email abaixo e fique por
              dentro de tudo!"
              inputBackground="#fff"
              inputColor={COLORS.black}
              origin="post"
              metadata={{
                post: {
                  uid: post.uid,
                  id: post.id,
                },
              }}
            />
          </GridBox>

          <Spacing top={4}>
            {post.data.categories.some((item) => item.category?.uid) && (
              <span style={{ fontSize: 12 }}>Navegue pelas categorias: </span>
            )}
            {post.data.categories.map(({ category }) =>
              category?.uid ? (
                <CategoryLink category={category} key={category?.uid} />
              ) : null,
            )}
          </Spacing>
        </Wrapper>
        <Section pastel style={{ textAlign: 'center' }}>
          <GridBox
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Heading3>Pronto para começar? Vamos fazer uma cotação!</Heading3>
            <Spacing vertical={2}>
              <Paragraph style={{ maxWidth: 700 }}>
                Na Luna, simplificamos o processo de compra do seguro para
                carros e motos para que você tenha a cobertura que precisa, de
                forma rápida e fácil!
              </Paragraph>
            </Spacing>

            <Link href={ROUTES.startForm()}>
              <a>
                <PrimaryButton onClick={() => trackGetQuoteClick('blog post')}>
                  Faça Parte
                </PrimaryButton>
              </a>
            </Link>
          </GridBox>
        </Section>
        {related.length > 0 && (
          <Section>
            <Heading3 center>Continue Lendo</Heading3>
            <Spacing top={4}>
              <GridBox justifyContent="flex-start">
                {related.map((p) => (
                  <PostItem post={p} key={p.id} />
                ))}
              </GridBox>
            </Spacing>
          </Section>
        )}
        <FixedGetQuoteButton />
      </PageContainer>
    </>
  );
};

export const getStaticProps: GetStaticProps<BlogPostPageProps> = async ({
  params,
  preview = false,
  previewData,
}) => {
  const post = (await PrismicClient.getByUID(
    'post',
    `${params?.uid}`,
    previewData as Record<string, any>,
  )) as Post;

  if (!post) return { notFound: true };

  const related = (
    await PrismicClient.query(
      [
        Prismic.Predicates.similar(post.id, 10),
        Prismic.Predicates.at('document.type', 'post'),
        Prismic.Predicates.fulltext(
          'document',
          RichText.asText(post.data.title),
        ),
      ],
      previewData as Record<string, any>,
    )
  ).results as Post[];

  return {
    props: {
      post,
      preview,
      related,
    },
    revalidate: 3600,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const allPosts = ['nomes-para-clinica-de-estetica'];

  return {
    paths: allPosts?.map((uid) => ROUTES.blogPost(uid)),
    fallback: true,
  };
};

export default PostPage;
